import { type ReactNode, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/error-boundary';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import NotFound from '@/pages/not-found';
import {
  Route,
  Switch,
  useLocation,
  Router as WouterRouter,
} from 'wouter';

import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import Home from '@/pages/Home';
import Privacy from '@/pages/Privacy';
import Imprint from '@/pages/Imprint';
import Team from '@/pages/Team';
import Meshes from '@/pages/Meshes';
import Blog from '@/pages/Blog';
import HeroSample from '@/pages/HeroSample';
import HeroBackgroundSample from '@/pages/HeroBackgroundSample';
import { HeroVideoBackground } from '@/components/HeroVideoBackground';
import AdminBlog from '@/pages/AdminBlog';

const queryClient = new QueryClient();

// Ensure scrolling to top when changing routes
function ScrollToTop() {
  const [pathname] = useLocation();
  useEffect(() => {
    if (window.location.hash) {
      const id = decodeURIComponent(window.location.hash.slice(1));
      requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView());
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

function Router() {
  const [location] = useLocation();
  const hideFooter = location === '/blog' || location === '/admin/blog';
  return (
    <div className="flex flex-col min-h-[100dvh]">
      <ScrollToTop />
      <Navbar solid={location === '/hero-sample' || location === '/hero-background-sample'} />
      <div className="flex-1">
        <RoutedErrorBoundary>
          <Switch>
            <Route path="/"><Home heroBackground={<HeroVideoBackground />} /></Route>
            <Route path="/hero-sample" component={HeroSample} />
            <Route path="/hero-background-sample" component={HeroBackgroundSample} />
            <Route path="/team" component={Team} />
            <Route path="/meshes" component={Meshes} />
            <Route path="/blog" component={Blog} />
            <Route path="/admin/blog" component={AdminBlog} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/imprint" component={Imprint} />
            <Route component={NotFound} />
          </Switch>
        </RoutedErrorBoundary>
      </div>
      {!hideFooter && <Footer />}
    </div>
  );
}

function RoutedErrorBoundary({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  return <ErrorBoundary resetKey={location}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
          <Router />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
